/**
 * Supabase Client
 *
 * Features:
 * 1. Token freshness gate — checks JWT expiry before every data request
 *    and refreshes if expired/near-expiry + patches Authorization header.
 *    Mutex-guarded to prevent concurrent refresh races.
 * 2. resilientFetch — timeout + automatic retry on network errors (stale TCP)
 */

import { createClient } from '@supabase/supabase-js'
import { config } from '@/config'

const supabaseUrl = config.supabase.url
const supabaseAnonKey = config.supabase.anonKey

if (!supabaseUrl || !supabaseAnonKey) {
    console.warn(
        'Supabase credentials not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file.'
    )
}

// ─── Token freshness gate ────────────────────────────────────────────

const REFRESH_MARGIN_S = 60
let refreshPromise: Promise<string | null> | null = null

function getJwtExp(token: string): number {
    try {
        const parts = token.split('.')
        if (parts.length !== 3) return 0
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
        return typeof payload.exp === 'number' ? payload.exp : 0
    } catch {
        return 0
    }
}

/**
 * If the access token is expired or near-expiry, refresh it.
 * ALWAYS returns a valid access_token string if authenticated, or null otherwise.
 * Mutex-guarded so only one refresh runs at a time.
 * @param signal Optional AbortSignal from the parent request
 */
export async function ensureTokenFresh(signal?: AbortSignal): Promise<string | null> {
    const storageKey = `sb-${new URL(supabaseUrl || 'https://placeholder.supabase.co').hostname.split('.')[0]}-auth-token`
    const raw = localStorage.getItem(storageKey)
    if (!raw) return null

    try {
        const stored = JSON.parse(raw)
        const accessToken: string | undefined = stored?.access_token
        if (!accessToken) return null

        const exp = getJwtExp(accessToken)
        if (exp === 0) return accessToken

        const nowS = Math.floor(Date.now() / 1000)
        const timeLeft = exp - nowS
        
        // If still very fresh, return immediately
        if (timeLeft > REFRESH_MARGIN_S) {
            return accessToken
        }

        /**
         * OPTIMISTIC AUTH:
         * If the token is near-expiry but still technically valid (e.g., > 5s left),
         * we trigger the refresh in the background but return the current token
         * immediately. This prevents a slow refresh from blocking the entire 
         * page load for 10s.
         */
        const canUseOptimistically = timeLeft > 5;

        // If a refresh is already in progress...
        if (refreshPromise) {
            if (canUseOptimistically) {
                console.log(`[Supabase] Using near-expiry token optimistically while refresh finishes background...`)
                return accessToken
            }

            console.log(`[Supabase] Waiting for existing refresh promise (Token is critical)...`)
            if (signal?.aborted) return accessToken

            return new Promise((resolve) => {
                const onAbort = () => {
                    console.log('[Supabase] Auth wait cancelled by caller')
                    resolve(accessToken || null)
                }
                signal?.addEventListener('abort', onAbort)
                
                refreshPromise!.then(token => {
                    signal?.removeEventListener('abort', onAbort)
                    resolve(token)
                }).catch(() => {
                    signal?.removeEventListener('abort', onAbort)
                    resolve(accessToken || null)
                })
            })
        }

        console.log(`[Supabase] ${new Date().toISOString()} Token near-expiry (${timeLeft}s left), triggering refresh...`)
        
        refreshPromise = (async () => {
            try {
                const { data, error } = await supabase.auth.refreshSession()
                if (error) throw error
                console.log(`[Supabase] Token refreshed OK`)
                return data.session?.access_token || null
            } catch (e: any) {
                console.warn(`[Supabase] Token refresh error:`, e?.message || e)
                return accessToken
            } finally {
                refreshPromise = null
            }
        })()

        if (canUseOptimistically) {
            console.log(`[Supabase] Proceeding optimistically; refresh will continue in background.`)
            return accessToken
        } else {
            console.log(`[Supabase] Token is critical/expired. Must await refresh before proceeding.`)
            return await refreshPromise
        }
    } catch {
        return null
    }
}

// ─── Resilient fetch ─────────────────────────────────────────────────

const resilientFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url

    // Improved auth endpoint detection
    const isAuthEndpoint =
        url.includes('/auth/v1/') ||
        url.includes('/token') ||
        url.includes('grant_type=') ||
        url.includes('refresh_token')

    // 1) Token freshness gate + header patching
    if (!isAuthEndpoint) {
        // Pass the request signal to the auth gate so we can stop waiting if the request is aborted
        const freshToken = await ensureTokenFresh(init?.signal || undefined)
        if (freshToken) {
            const headers = new Headers(init?.headers || {})
            headers.set('Authorization', `Bearer ${freshToken}`)
            init = { ...init, headers }
        }
    }

    // 2) Keep the original signal reference if provided
    const externalSignal = init?.signal

    // 3) Internal attempt logic with dual-signal support
    const attempt = async (): Promise<Response> => {
        if (externalSignal?.aborted) {
            throw new DOMException('Request aborted by caller', 'AbortError')
        }

        const internalController = new AbortController()
        const timeoutMs = 15000;
        const timer = setTimeout(() => {
            console.warn(`[Supabase] Request timed out after ${timeoutMs}ms:`, url.split('?')[0])
            internalController.abort()
        }, timeoutMs)

        // Link external signal to internal controller
        const onExternalAbort = () => {
            internalController.abort()
        }
        if (externalSignal) {
            externalSignal.addEventListener('abort', onExternalAbort)
        }

        try {
            return await fetch(input, {
                ...init,
                signal: internalController.signal
            })
        } finally {
            clearTimeout(timer)
            if (externalSignal) {
                externalSignal.removeEventListener('abort', onExternalAbort)
            }
        }
    }

    // 4) Try with retry logic
    try {
        return await attempt()
    } catch (firstError: any) {
        // If the error is an ABORT due to the EXTERNAL signal, do NOT retry.
        // We only retry on internal timeouts or network failures.
        if (externalSignal?.aborted) throw firstError

        const isRetryable =
            firstError?.name === 'AbortError' || // Internal timeout
            firstError?.name === 'TypeError' || // Network failure
            (firstError?.message && (
                firstError.message.includes('fetch') ||
                firstError.message.includes('NetworkError') ||
                firstError.message.includes('Failed to fetch')
            ))

        if (!isRetryable) throw firstError

        console.warn(`[Supabase] ${new Date().toISOString()} Fetch failed (${firstError.name}: ${firstError.message}), retrying...`, url.split('?')[0])

        try {
            return await attempt()
        } catch (retryError: any) {
            if (externalSignal?.aborted) throw retryError
            console.error(`[Supabase] Retry also failed:`, retryError?.message || retryError)
            throw retryError
        }
    }
}

export const supabase = createClient(
    supabaseUrl || 'https://placeholder.supabase.co',
    supabaseAnonKey || 'placeholder-key',
    {
        auth: {
            autoRefreshToken: true,
            persistSession: true,
            detectSessionInUrl: true,
        },
        global: {
            fetch: resilientFetch
        }
    }
)

export function isAbortError(e: any): boolean {
    if (!e) return false
    return e.name === 'AbortError' || 
           e.message?.includes('AbortError') || 
           e.message?.includes('aborted') ||
           e.hint?.includes('aborted')
}

export default supabase
